import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageCircle, Mail, Video, Paperclip, Send, X, Phone, MapPin, Clock,
  Image as ImageIcon, Pencil, Trash2,
} from "lucide-react";
import { STAGES, PLAN_META, formatBRL } from "@/data/backofficeData";
import { VideoRoom } from "./VideoRoom";

const TABS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "E-mail", icon: Mail },
  { id: "video", label: "Videoconferência", icon: Video },
];

const HISTORY_ICON = { whatsapp: MessageCircle, email: Mail, video: Video, system: Clock };

export const LeadSlideOver = ({ lead, onClose, onMove, onHistory, onEdit, onRemove }) => {
  const [tab, setTab] = useState("whatsapp");
  const [wpp, setWpp] = useState("");
  const [files, setFiles] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (lead) {
      setWpp(`Olá ${lead.contact || lead.name}! Aqui é da VeloClin. Tudo bem? Gostaria de te mostrar em poucos minutos como a plataforma ajuda médicos independentes a organizar a agenda, atender por telemedicina e receber no Pix sem intermediários. Podemos conversar?`);
    }
  }, [lead?.id]);

  if (!lead) return null;

  const sendWpp = () => {
    if (!wpp.trim()) return toast.error("Escreva uma mensagem.");
    const digits = (lead.phone || "").replace(/\D/g, "");
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(wpp.trim())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onHistory({ type: "whatsapp", text: wpp.trim(), at: new Date().toLocaleString("pt-BR") });
    toast.success("WhatsApp aberto no navegador", { description: lead.phone || "sem telefone cadastrado" });
  };

  const insertImages = (fileList) => {
    Array.from(fileList).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const img = document.createElement("img");
        img.src = reader.result;
        img.className = "my-2 max-w-full rounded-lg border border-slate-200";
        editorRef.current?.appendChild(img);
        editorRef.current?.appendChild(document.createElement("br"));
      };
      reader.readAsDataURL(file);
    });
    toast.success(`${fileList.length} imagem(ns) inserida(s) no corpo`);
  };

  const sendEmail = () => {
    const text = editorRef.current?.textContent?.trim() || "";
    const imgCount = editorRef.current?.querySelectorAll("img").length || 0;
    if (!text && imgCount === 0) return toast.error("Escreva o corpo do e-mail.");
    const parts = [];
    if (text) parts.push(text.slice(0, 120));
    if (imgCount) parts.push(`${imgCount} imagem(ns) no corpo`);
    if (files.length) parts.push(`${files.length} anexo(s)`);
    onHistory({ type: "email", text: parts.join(" · "), at: new Date().toLocaleString("pt-BR") });
    toast.success("E-mail registrado para envio", { description: lead.email });
    if (editorRef.current) editorRef.current.innerHTML = "";
    setFiles([]);
  };

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="lead-slideover" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
        {/* header */}
        <div className="bg-slate-900 px-6 py-5 text-white">
          <SheetHeader className="space-y-0 text-left">
            <div className="flex items-start justify-between gap-3">
              <SheetTitle className="text-lg font-extrabold text-white">{lead.name}</SheetTitle>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${PLAN_META[lead.plan].color}`}>{PLAN_META[lead.plan].label} · {formatBRL(lead.value)}/mês</span>
            </div>
            <SheetDescription className="text-slate-400">{lead.contact} · {lead.type === "clinica" ? "Clínica" : "Médico(a)"}{lead.specialty ? ` · ${lead.specialty}` : ""}</SheetDescription>
          </SheetHeader>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-300">
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>
            {lead.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.city}</span>}
          </div>

          {/* editar / remover */}
          <div className="mt-3 flex gap-2">
            <button data-testid="lead-edit-btn" onClick={onEdit} className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/20">
              <Pencil className="h-3.5 w-3.5" /> Editar
            </button>
            <button data-testid="lead-remove-btn" onClick={() => setConfirmOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-rose-500/15 px-2.5 py-1.5 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-500/25">
              <Trash2 className="h-3.5 w-3.5" /> Remover
            </button>
          </div>

          {/* stage selector */}
          <div className="mt-4">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Estágio no funil</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`lead-stage-${s.id}`}
                  onClick={() => { onMove(s.id); toast.success(`Movido para ${s.label}`); }}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${lead.stage === s.id ? "bg-white text-slate-900" : "bg-white/10 text-slate-300 hover:bg-white/20"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className="flex border-b border-slate-200 bg-white">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`lead-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-xs font-semibold transition-colors ${tab === id ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-400 hover:text-slate-600"}`}
            >
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>

        <div className="flex-1 space-y-4 p-6">
          {tab === "whatsapp" && (
            <div className="space-y-3" data-testid="lead-whatsapp-panel">
              <Textarea data-testid="wpp-textarea" value={wpp} onChange={(e) => setWpp(e.target.value)} rows={4} placeholder={`Mensagem para ${lead.contact}...`} className="resize-none" />
              <button data-testid="wpp-send-btn" onClick={sendWpp} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-700">
                <MessageCircle className="h-4 w-4" />Abrir no WhatsApp
              </button>
              <p className="text-center text-xs text-slate-400">Abre a conversa no WhatsApp Web/app com a mensagem já preenchida para você enviar.</p>
            </div>
          )}

          {tab === "email" && (
            <div className="space-y-3" data-testid="lead-email-panel">
              {/* corpo com imagens */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-testid="email-body-editable"
                data-placeholder="Escreva o corpo do e-mail... (você pode inserir imagens no corpo)"
                className="min-h-[140px] w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
              <input ref={imageRef} type="file" accept="image/*" multiple data-testid="email-image-input" className="hidden" onChange={(e) => { if (e.target.files?.length) insertImages(e.target.files); e.target.value = ""; }} />
              <input ref={fileRef} type="file" multiple data-testid="email-file-input" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              <div className="flex gap-2">
                <button data-testid="email-insert-image-btn" onClick={() => imageRef.current?.click()} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-dashed border-blue-300 py-2.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50">
                  <ImageIcon className="h-4 w-4" />Inserir imagem no corpo
                </button>
                <button data-testid="email-attach-btn" onClick={() => fileRef.current?.click()} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                  <Paperclip className="h-4 w-4" />Anexar arquivos
                </button>
              </div>
              {files.length > 0 && (
                <div className="space-y-1.5" data-testid="email-file-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                      <span className="truncate text-slate-700">{f.name}</span>
                      <button onClick={() => setFiles((list) => list.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <button data-testid="email-send-btn" onClick={sendEmail} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700">
                <Send className="h-4 w-4" />Enviar E-mail
              </button>
            </div>
          )}

          {tab === "video" && <VideoRoom lead={lead} onHistory={onHistory} />}

          {/* histórico */}
          <div className="pt-2">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Histórico de Interações</p>
            <div className="space-y-2" data-testid="lead-history">
              {lead.history.map((h) => {
                const Icon = HISTORY_ICON[h.type] || Clock;
                return (
                  <div key={h.id} className="flex gap-2.5 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm"><Icon className="h-3.5 w-3.5" /></div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700">{h.text}</p>
                      <p className="mt-0.5 text-[10px] text-slate-400">{h.at}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="remove-lead-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remover lead?</AlertDialogTitle>
              <AlertDialogDescription>O lead <b>{lead.name}</b> será removido do funil comercial. Não poderá ser desfeito.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="remove-lead-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="remove-lead-confirm" onClick={onRemove} className="bg-rose-600 hover:bg-rose-700">Remover</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};
